import clsx from 'clsx'
import { GetStaticProps } from 'next'
import Layout from '@/templates/Layout/CMLLayout'
import HeroSection from '@/templates/Influencer/HeroSection'
import CustomerLogo from '@/templates/Influencer/CustomerLogo'
import CMLCategory from '@/templates/Influencer/CMPCategory'
import FeaturedInfluencer from '@/templates/Influencer/FeaturedInfluencer'
import InfluencerGetStarted from '@/templates/Influencer/InfluencerGetStarted'
import InfluencerFooterCta from '@/templates/Influencer/InfluencerFooterCta'
import CmpTestimonial from '@/templates/Influencer/CmpTestimonial'
import CMLList from '@/templates/Influencer/CMLList/CMLList'

/* utils */
import { getStrapiGraphqlData } from '@/utils/api/wrapper'
import { influencerPageQuery } from '@/utils/queries/influencer'
import { INSTANT_REVALIDATION } from '@/utils/constants/revalidation'
import { capture404PageError } from '@/utils/functions/elk-server'

/* styling */
import styles from './_.module.css'

interface Props {
  influencerPage: InfluencerPage
}

function InfluencerPage({ influencerPage }: Props): JSX.Element {
  const metadata = [
    {
      property: 'og:title',
      content: influencerPage.Seo.title
    },
    {
      property: 'description',
      content: influencerPage.Seo.description ?? ''
    },
    {
      property: 'og:description',
      content: influencerPage.Seo.description ?? ''
    },
    {
      property: 'og:image',
      content: influencerPage.Seo.featuredImage.data?.attributes.url ?? ''
    }
  ]

  return (
    <Layout
      title={influencerPage.Seo.title}
      canonical="/global-marketing-leaders/"
      meta={metadata}
    >
      <div className={clsx(styles.container, 'text-white')}>
        <HeroSection
          heroDescription={influencerPage.heroDescription}
          heroImage={influencerPage.heroImage}
          heroTitleImage={influencerPage.heroTitleImage}
          heroSubtext={influencerPage.heroSubtext}
          primaryCta={influencerPage.heroPrimaryCta}
          secondaryCta={influencerPage.heroSecondaryCta}
        />

        {influencerPage.brands ? (
          <CustomerLogo
            title={influencerPage.brands.title}
            brands={influencerPage.brands?.brands.data.map(i => ({
              ...i.attributes.logo.data.attributes,
              alternativeText: i.attributes.name
            }))}
          />
        ) : null}

        <CMLCategory />

        <section className="container">
          {influencerPage.featuredInfluencers?.data.length > 0 ? (
            <FeaturedInfluencer
              className="py-5 md:py-18"
              influencerTitle={influencerPage?.influencerTitle}
              featuredInfluencers={influencerPage.featuredInfluencers.data.map(
                i => i.attributes
              )}
            />
          ) : null}
          <CMLList showFilters={false} />
        </section>

        {influencerPage.cmpTestimonialList.length > 0 ? (
          <CmpTestimonial
            title={influencerPage.cmpTestimonialTitle}
            testimonials={influencerPage.cmpTestimonialList}
          />
        ) : null}

        <InfluencerGetStarted
          mode="full_width"
          variant="dark"
          primaryHeadline={influencerPage.getStarted.title ?? ''}
          subText={influencerPage.getStarted.description ?? ''}
          image={influencerPage.getStarted.multimedia?.media?.data?.attributes}
          primaryCta={influencerPage.getStarted.link}
        />

        <InfluencerFooterCta
          primaryHeadline={influencerPage.footerCta.title ?? ''}
          image={influencerPage.footerCta.multimedia?.media?.data?.attributes}
          primaryCta={influencerPage.footerCta.link}
          tag={
            influencerPage.footerCta.additionalFields?.tag as string | undefined
          }
        />
      </div>
    </Layout>
  )
}

export const getStaticProps: GetStaticProps = async () => {
  const result = await getStrapiGraphqlData<InfluencerPageQuery>(
    influencerPageQuery.loc?.source.body
  )

  const page = result?.influencerPage?.data?.attributes

  if (!page) {
    capture404PageError(`/global-marketing-leaders/`)
    return { notFound: true, revalidate: INSTANT_REVALIDATION }
  }

  return {
    props: {
      influencerPage: page
    },
    revalidate: INSTANT_REVALIDATION
  }
}

export default InfluencerPage
